import { useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';

import type { TranslationDto } from '@/services/ws-translate/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useLocation } from '@/js/libs/router';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage.ts';
import { clearTrackedKeys, disableTracking, enableTracking, subscribeToTrackedKeys } from './tracker/index.ts';

export const { useTranslateModeContext } = createContexts({
  TranslateMode() {
    const localStorage = useLocalStorage();
    const location = useLocation();

    const [isEnabled, setIsEnabled] = useState(localStorage.loadString(StorageKey.TRANSLATE_MODE_ENABLED) === 'true');
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [trackedKeys, setTrackedKeys] = useState<string[]>([]);

    const editorModalState = useModalState<TranslationDto | undefined>({ onClose: () => setIsOverlayOpen(true) });

    const toggleEnabled = useCallbackRef(() => {
      setIsEnabled(!isEnabled);
      setIsOverlayOpen(!isEnabled);
    });

    const openOverlay = useCallbackRef(() => {
      setIsOverlayOpen(true);
    });

    const closeOverlay = useCallbackRef(() => {
      setIsOverlayOpen(false);
    });

    // Clears the tracked keys on enabling and on every page change. Declared before the tracking effect: the list has to
    // be empty before that effect's re-render refills it.
    useEffect(() => {
      if (isEnabled) {
        clearTrackedKeys();
      }
    }, [location.pathname, isEnabled]);

    // Turns tracking on or off. On enabling it re-renders every translated component, which refills the list the clearing
    // effect above has just emptied with the keys already on screen; the two effects run in declaration order.
    useEffect(() => {
      if (isEnabled) {
        enableTracking();
        void i18n.changeLanguage(i18n.language);
      } else {
        disableTracking();
        setIsOverlayOpen(false);
      }
    }, [isEnabled]);

    useEffect(() => {
      const unsubscribe = subscribeToTrackedKeys((keys) => {
        setTrackedKeys(Array.from(keys));
      });
      return unsubscribe;
    }, []);

    useEffect(() => {
      localStorage.saveString(StorageKey.TRANSLATE_MODE_ENABLED, String(isEnabled));
    }, [isEnabled, localStorage]);

    return useMemo(
      () => ({
        editorModalState,
        isEnabled,
        isOverlayOpen,
        trackedKeys,
        toggleEnabled,
        openOverlay,
        closeOverlay,
      }),
      [editorModalState, isEnabled, isOverlayOpen, trackedKeys, toggleEnabled, openOverlay, closeOverlay],
    );
  },
});
